import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { EnvelopeIcon } from '@heroicons/react/24/outline';

import { COMMUNITY_ROUTES } from '~/constants/community';
import type { CommunitySearchMember } from '~/interfaces/community';

import { Avatar } from '../ui/Avatar';
import { MemberBadge } from '../ui/MemberBadge';
import { MemberProfileLink } from '../ui/MemberProfileLink';
import { PillLink } from '../ui/PillLink';

import { useCanMessageMember } from '../../_hooks/useCanMessageMember';

interface MobileMemberResultRowProps {
  member: CommunitySearchMember;
  onClose: () => void;
}

export const MobileMemberResultRow: FC<MobileMemberResultRowProps> = ({ member, onClose }) => {
  const { t } = useTranslation();
  const canMessage = useCanMessageMember(member);

  return (
    <div className="flex items-center gap-4 p-4">
      <MemberProfileLink memberId={member.id} onClick={onClose} className="flex items-center gap-4 min-w-0 flex-1">
        <Avatar src={member.avatar_url} alt={member.name} className="size-10" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[6px] min-w-0">
            <p className="font-semibold text-[15px] leading-5 text-black truncate">{member.name}</p>
            <MemberBadge
              roles={member.roles}
              isBlocked={member.computed?.blocking === true}
              blockedYou={member.computed?.blocked_you === true}
              badges={member.badges}
              limit={1}
            />
          </div>
          {member.headline && <p className="text-[11px] leading-4 text-black/50 truncate">{member.headline}</p>}
        </div>
      </MemberProfileLink>
      {canMessage && (
        <PillLink
          variant="primary"
          size="sm"
          to={COMMUNITY_ROUTES.chatDm(member.id)}
          onClick={onClose}
          className="shrink-0 flex items-center justify-center gap-1 px-4"
        >
          <EnvelopeIcon className="size-4" />
          {t('community.search.message')}
        </PillLink>
      )}
    </div>
  );
};
